import { formatPersonFullName } from "@/utils/formatters";
import type { Nurse, NurseData } from "@/utils/models";
import { type AddWarningContext, type AddWarnings, classifyAddWarnings } from "@/utils/warningDialogs/classifyAddWarnings";
import type { EmployeeStatusWarningProps } from "@/utils/warningDialogs/employeeStatusWarning";
import type { TargetCheckResult } from "@/utils/warningDialogs/useStaffingTargetCheck";

/** One picked nurse, as the review names them. */
export type MultiAddReviewNurse = { nurseId: number; name: string };

/**
 * Everything a multi-add should confirm before it publishes, grouped by warning — the batch version of
 * the single add's dialog cascade. A nurse can sit in several groups; `clear` holds the ones in none.
 */
export type MultiAddReview = {
	/** On Leave / Suspended on the shift's date (a safety net: the picker blocks them already). */
	employeeStatus: (MultiAddReviewNurse & Pick<EmployeeStatusWarningProps, "status" | "endDate">)[];
	pto: MultiAddReviewNurse[];
	/** Paid hours that day including this shift, over the department's long-day limit. */
	longDay: (MultiAddReviewNurse & { hours: number })[];
	/** Predicted overtime hours this shift adds, with its display cost (`"-"` when there is no prediction). */
	overtime: (MultiAddReviewNurse & { hours: number; cost: string })[];
	clear: MultiAddReviewNurse[];
	/** The location's staffing target once all N are on it — `null` unless the add goes over it. */
	target: TargetCheckResult | null;
};

export type MultiAddReviewInputs = AddWarningContext & {
	/** The picked staff, in pick order. */
	nurses: readonly Nurse[];
	/** Their grid rows — the review reads the same overtime / daily-hours figures each row shows. */
	rows: readonly NurseData[];
	/** ONE staffing-target check for the whole batch (`addCount` = N). */
	target: TargetCheckResult;
	/** The department's name-order flag (`useLastNameFirst()`). */
	lastNameFirst: boolean;
};

const hasAnyWarning = ({ employeeStatus, pto, longDayHours, overtime }: AddWarnings): boolean =>
	!!employeeStatus || pto || longDayHours !== null || overtime !== null;

export const buildMultiAddReview = (inputs: MultiAddReviewInputs): MultiAddReview => {
	const review: MultiAddReview = { employeeStatus: [], pto: [], longDay: [], overtime: [], clear: [], target: null };
	for (const nurse of inputs.nurses) {
		const who: MultiAddReviewNurse = { nurseId: nurse.id, name: formatPersonFullName(nurse, inputs.lastNameFirst) };
		// The single add's own rule, so one add and a batch of the same nurses always warn alike.
		const warnings = classifyAddWarnings(
			nurse,
			inputs.rows.find((row) => row.id === nurse.id),
			inputs,
		);
		if (warnings.employeeStatus) review.employeeStatus.push({ ...who, status: warnings.employeeStatus.status, endDate: warnings.employeeStatus.endDate });
		if (warnings.pto) review.pto.push(who);
		if (warnings.longDayHours !== null) review.longDay.push({ ...who, hours: warnings.longDayHours });
		if (warnings.overtime) review.overtime.push({ ...who, ...warnings.overtime });
		if (!hasAnyWarning(warnings)) review.clear.push(who);
	}
	// Coverage targets don't apply to worked-time entries — the single add's target gate skips them too.
	if (!inputs.isActual && inputs.target.exceeded) review.target = inputs.target;
	return review;
};

/** Whether the batch needs the review dialog at all — no warnings publishes straight away. */
export const hasMultiAddWarnings = (review: MultiAddReview): boolean =>
	review.target !== null || review.employeeStatus.length > 0 || review.pto.length > 0 || review.longDay.length > 0 || review.overtime.length > 0;
